import { medcenterAdapter, doctorsInMedcenterAdapter, adapterMedcenterGallery } from '../adapters/medcenters';

import type { IStandardResponse, IResponseDataList, IPagination } from '@/entities/common/types/api';
import type { IGallery } from '@/entities/common/types/gallery';
import type { IBaseDoctor } from '@/entities/doctor/model/types';
import type {
  IMedcenterDetails,
  IMedcenterService,
  SearchMedcenterServicesQuery,
} from '@/entities/medcenter/model/types';
import type { Skill } from '@/entities/skill/model/types';

import type { HttpClient } from '../http';

interface GetDoctorsInMedcenterParams {
  page: number;
  skillId?: number;
}

class MedcenterRepository {
  private http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  private getDefaultPagination(page: number): IPagination {
    return {
      perPage: 0,
      currentPage: page,
      hasNextPage: false,
      total: 0,
    };
  }

  public async getMedcenterByAlias(alias: string): Promise<IMedcenterDetails | null> {
    try {
      const data = await this.http.get<IStandardResponse<IMedcenterDetails>>(`/medcenters/${alias}`);

      return medcenterAdapter(data.data);
    } catch (error) {
      console.error('Error fetching medcenter data:', error);

      return null;
    }
  }

  public async getMedcenterServiceByAlias(
    alias: string,
    page: number,
  ): Promise<{ data: IMedcenterService[]; pagination: IPagination }> {
    try {
      const data = await this.http.get<IResponseDataList<IMedcenterService>>(
        `/medcenters/${alias}/services`,
        {},
        { page },
      );

      return {
        data: data.data,
        pagination: data.pagination,
      };
    } catch (error) {
      console.error('Error fetching medcenter services:', error);

      return {
        data: [],
        pagination: this.getDefaultPagination(page),
      };
    }
  }

  public async searchMedcenterServices(
    alias: string,
    searchQuery: SearchMedcenterServicesQuery,
  ): Promise<{ data: IMedcenterService[]; pagination: IPagination }> {
    try {
      const data = await this.http.get<IResponseDataList<IMedcenterService>>(
        `/medcenters/${alias}/services/search`,
        {},
        {
          page: searchQuery.page,
          q: searchQuery.searchTerm,
        },
      );
      return {
        data: data.data,
        pagination: data.pagination,
      };
    } catch (error) {
      console.error('Error searching medcenter services:', error);
      return {
        data: [],
        pagination: this.getDefaultPagination(searchQuery.page),
      };
    }
  }

  public async getDoctorSkillsInMedcenter(alias: string): Promise<Skill[]> {
    try {
      const data = await this.http.get<IStandardResponse<Skill[]>>(`/medcenters/${alias}/doctors/skills`);

      return data.data;
    } catch (error) {
      console.error('Error fetching doctor skills:', error);

      return [];
    }
  }

  public async getDoctorsInMedcenter(
    alias: string,
    { page, skillId }: GetDoctorsInMedcenterParams,
  ): Promise<{ data: IBaseDoctor[]; pagination: IPagination }> {
    try {
      const queryParams = {
        page,
        ...(typeof skillId === 'number' && { skillId }),
      };

      const data = await this.http.get<IResponseDataList<IBaseDoctor>>(`/medcenters/${alias}/doctors`, {}, queryParams);

      return {
        data: doctorsInMedcenterAdapter(data.data),
        pagination: data.pagination,
      };
    } catch (error) {
      console.error('Error fetching doctors:', error);
      return {
        data: [],
        pagination: this.getDefaultPagination(page),
      };
    }
  }

  public async getMedcenterGallery(alias: string): Promise<IGallery[]> {
    try {
      const data = await this.http.get<IStandardResponse<IGallery[]>>(`/medcenters/${alias}/gallery`);

      return adapterMedcenterGallery(data.data);
    } catch (error) {
      console.error('Error fetching medcenter gallery:', error);

      return [];
    }
  }

  public async getMedcenterSkills(alias: string): Promise<Skill[]> {
    try {
      const endpoint = `/medcenters/${alias}/doctors/skills`;
      const response = await this.http.get<IResponseDataList<Skill>>(endpoint);
      const skills = response.data;

      return skills;
    } catch (error) {
      console.error('Error fetching skills:', error);
      return [];
    }
  }
}

export { MedcenterRepository };
